import { ReactElement, useState } from "react";
import {Button,Col,Drawer,Form,Input,Row,TimePicker,Upload,message,Select } from "antd";
import firebase from "firebase/app";
import "firebase/storage";
import { InboxOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DrawerFooter } from "../../components/GlobalStyles";
import { EDIT_BRANCH_REQUEST } from "../../redux/constants";
import { getAuthState } from "../../redux/selectors";

const { useForm } = Form;
const { Option } = Select;

type Props = {
  visible: boolean;
  onClose: () => void;
  branch: any;
};

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  return false;
}

export function EditBranchDrawer({
  visible,
  onClose,
  branch,
}: Props): ReactElement {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(getAuthState);
  const [form] = useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [femaleOnly, setFemaleOnly] = useState<any[]>(branch.femaleOnly);
  const [startTime, setStartTime] = useState<any>(moment(`${moment().format("YYYY-MM-DD")} ${branch.startTime}`));
  const [endTime, setEndTime] = useState<any>(moment(`${moment().format("YYYY-MM-DD")} ${branch.endTime}`));
  const storage = firebase.storage();

  const onFinish = async (values: any) => {
    setLoading(true);
    const updatedFormValue = {
      ...values,
      availability: [
        moment(values.startTime).format("HH:mm"),
        moment(values.endTime).format("HH:mm"),
      ],
      key: branch.firestoreRef,
      club: branch.club,
      femaleOnly,
      currentUser,
    };

    if (values.photo) {
      const ref = `clubs/${values.name}/${values.photo.file.name}`;
      await storage.ref(ref).put(values.photo.file);
      const clubImageUrl = await storage.ref(ref).getDownloadURL();
      updatedFormValue.photo = clubImageUrl;
    } else {
      updatedFormValue.photo = branch.photo;
    }

    Object.keys(updatedFormValue).forEach((key) => {
      if (updatedFormValue[key] === undefined) updatedFormValue[key] = "";
    });

    console.log(updatedFormValue);
    dispatch({
      type: EDIT_BRANCH_REQUEST,
      payload: updatedFormValue,
    });
    setLoading(false);
    form.resetFields();
    onClose();
  };

  const onFinishFailed = (errorInfo: any) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const onAddFemaleDay = () => {
    const one = {
      day : "1",
      startTime: "",
      endTime: "",
    }
    const m = [...femaleOnly, one];
    setFemaleOnly(m);
  }
  const onChangeFemaleDay = (value: string, index: number) => {
    const mFemaleDays = [...femaleOnly];
    mFemaleDays[index].day = value;
    setFemaleOnly(mFemaleDays);
  }
  const onChangeFemaleDayStartTime = (value: any, index: number) => {
    const mFemaleDays = [...femaleOnly];
    mFemaleDays[index].startTime = value.format("HH:mm");
    setFemaleOnly(mFemaleDays);
  }
  const onChangeFemaleDayEndTime = (value: any, index: number) => {
    const mFemaleDays = [...femaleOnly];
    mFemaleDays[index].endTime = value.format("HH:mm");
    setFemaleOnly(mFemaleDays);
  }
  const disabledHoursForFemaleOnly = () => {
    const startHour = startTime.hour();
    const endHour = endTime.hour();
    const disabledHours = [];
    if(startHour < endHour){
      for(let i = 0; i < 24; i+=1){
        if(i < startHour || i > endHour) disabledHours.push(i);
      }
    } else {
      for(let i = 0; i < 24; i+=1){
        if(i < startHour && i > endHour) disabledHours.push(i);
      }
    }
    return disabledHours;
  }

  const removeFemaleOnly = (index: number) => {
    const m = [...femaleOnly];
    const fm = m.filter((f, mIndex) => index !== mIndex);
    setFemaleOnly(fm);
  }


  return (
    <Drawer
      title={t("branch.edit_branch")}
      width={720}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          location: branch.location,
          linkLocation: branch.linkLocation,
          startTime: moment(`${moment().format("YYYY-MM-DD")} ${branch.startTime}`),
          endTime : moment(`${moment().format("YYYY-MM-DD")} ${branch.endTime}`),
          services: branch.services,
        }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="location"
              label={t("branch.location")}
              rules={[
                {
                  required: true,
                  message: t("branch.location_placeholder"),
                },
              ]}
            >
              <Input placeholder={t("branch.location_placeholder")} autoComplete="off"/>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item name="photo" label="Photo">
              <Upload.Dragger
                name="files"
                beforeUpload={beforeUpload}
                showUploadList
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint"> Support for a single upload.</p>
              </Upload.Dragger>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="linkLocation"
              label={t("branch.link_location")}
              rules={[
                {
                  required: true,
                  message: t("branch.link_location_placeholder"),
                },
              ]}
            >
              <Input
                style={{ width: "100%" }}
                placeholder={t("branch.link_location_placeholder")}
                autoComplete="off"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="startTime"
              label={t("branch.startTime")}
              rules={[
                {
                  required: true,
                  message:t("branch.available_hours_placeholder"),
                }
              ]}
            >
              <TimePicker format="HH:mm" minuteStep={30}/>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="endTime"
              label={t("branch.endTime")}
              rules={[
                {
                  required: true,
                  message:t("branch.available_hours_placeholder"),
                }
              ]}
            >
              <TimePicker format="HH:mm" minuteStep={30}/>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <p>Female only time</p>
          </Col>
        </Row>
        {femaleOnly.map((f, index) => (
          <Row gutter={24} style={{ marginBottom : 10}}>
            <Col span={12}>
              <Select
                value={f.day}
                onChange={(value) => onChangeFemaleDay(value, index)}
                style={{width: "100%"}}
              >
                <Option value="1">{t("branch.female_only_day_mon")}</Option>
                <Option value="2">{t("branch.female_only_day_tue")}</Option>
                <Option value="3">{t("branch.female_only_day_wed")}</Option>
                <Option value="4">{t("branch.female_only_day_thr")}</Option>
                <Option value="5">{t("branch.female_only_day_fri")}</Option>
                <Option value="6">{t("branch.female_only_day_sat")}</Option>
                <Option value="0">{t("branch.female_only_day_sun")}</Option>
              </Select>
            </Col>
            <Col span={5}>
              <TimePicker 
                format="HH:mm" 
                placeholder="Start Time" 
                style={{width : "100%"}} 
                onChange={(value) => onChangeFemaleDayStartTime(value, index)}
                disabledHours={disabledHoursForFemaleOnly}
                value={moment(`${moment().format("YYYY-MM-DD")} ${f.startTime}`)}
                minuteStep={30}
              />
            </Col>
            <Col span={5}>
              <TimePicker
                format="HH:mm" 
                placeholder="End Time"
                style={{width : "100%"}}
                onChange={(value) => onChangeFemaleDayEndTime(value, index)} 
                disabledHours={disabledHoursForFemaleOnly}
                value={moment(`${moment().format("YYYY-MM-DD")} ${f.endTime}`)}
                minuteStep={30}
              />
            </Col>
            <Col span={2}>
              <Button 
                danger 
                type="primary" 
                shape="circle" 
                icon={<DeleteOutlined />} 
                size="small" 
                onClick={() => removeFemaleOnly(index)}
              />
            </Col>
          </Row>
        ))}
        <Row gutter={24}>
          <Col span={24}>
            <Button style={{ width : "100%"}} onClick={onAddFemaleDay}>Add Female Day</Button>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="services"
              label={t("branch.services")}
              rules={[
                {
                  required: true,
                  message: t("branch.services_placeholder"),
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder={t("branch.services_placeholder")}
                autoComplete="off"
              />
            </Form.Item>
          </Col>
        </Row>

        <DrawerFooter>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            {t("common.cancel")}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("common.submit")}
          </Button>
        </DrawerFooter>
      </Form>
    </Drawer>
  );
}
